import DateFilterTemplate from 'apps/marsinet/src/app/components/date-template/date-template';
import { MarsinetContext } from 'apps/marsinet/src/app/context/marsinetProvider';
import { getDeviceDisplayValue } from 'apps/marsinet/src/app/helpers/deviceDisplayName';
import {
  formatDateTime,
  getIncidentPriorityColor,
  getIncidentStatusColor,
} from '../../commons/rps-incidence-helpers';
import { useDeviceNames } from 'apps/marsinet/src/app/hooks/useDeviceName';
import { MaintenanceOrderTableDto } from 'apps/marsinet/src/app/typescript-axios';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMeta,
  DataTablePageEvent,
} from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiServices } from '../../../../../services/apiServices';
import { incidenceBodyTemplate } from '../../commons/utils';
import {
  getCalendarDateInTimeZone,
  getUtcDayRange,
} from '../../../../../utils/timezone-utils';

/* -------------------------------------------------------------------------- */
/* Types                                                                       */
/* -------------------------------------------------------------------------- */

export enum GetIncidencesQueryDtoStatusEnum {
  All = 'all',
  Open = 'open',
  Closed = 'closed',
}

/**
 * The DTO exposes dates as ISO strings, but PrimeReact's date filters compare
 * against real Date instances. We widen each row once instead of parsing on
 * every filter pass.
 */
type MaintenanceOrderRow = MaintenanceOrderTableDto & {
  creationDate: Date | null;
};


interface MaintenanceOrdersListProps {
  refreshKey?: number;
  /** Open/closed scope owned by the page toolbar. */
  status?: 'open' | 'closed';
  /** Date range owned by the page toolbar; `null` means "no date filter". */
  dateRange?: Date[] | null;
  /** Bumped by the toolbar's "clear" button to reset the column filters. */
  clearFiltersKey?: number;
  /** Shared filters toolbar, rendered as the table header. */
  header?: React.ReactNode;
}

/* -------------------------------------------------------------------------- */
/* Pure helpers                                                                */
/* -------------------------------------------------------------------------- */


const buildInitialFilters = (): DataTableFilterMeta => ({
  code: { value: null, matchMode: FilterMatchMode.CONTAINS },
  description: { value: null, matchMode: FilterMatchMode.CONTAINS },
  device_serial_number: { value: null, matchMode: FilterMatchMode.CONTAINS },
  responsible: { value: null, matchMode: FilterMatchMode.CONTAINS },
  status: { value: null, matchMode: FilterMatchMode.EQUALS },
  creationDate: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
  },
});

const parseDate = (
  value: string | null | undefined,
  timeZone: string
): Date | null => {
  if (!value) return null;

  if (Number.isNaN(new Date(value).getTime())) return null;
  // El filtro de la tabla compara días del calendario, no instantes UTC.
  return getCalendarDateInTimeZone(value, timeZone);
};

const normalizeStatus = (value: unknown): string =>
  String(value ?? '')
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_');

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

export function MaintenanceOrdersList({
  refreshKey = 0,
  status = 'open',
  dateRange = null,
  clearFiltersKey = 0,
  header,
}: MaintenanceOrdersListProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { setError, selectedTimezone } = useContext(MarsinetContext);
  const { deviceNames } = useDeviceNames();

  const statusFilter =
    status === 'closed'
      ? GetIncidencesQueryDtoStatusEnum.Closed
      : GetIncidencesQueryDtoStatusEnum.Open;

  // Closed orders are read-only: nothing left to assign or close.
  const [orders, setOrders] = useState<MaintenanceOrderTableDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<DataTableFilterMeta>(
    buildInitialFilters
  );
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [sortField, setSortField] = useState<string | undefined>('creationDate');
  const [sortOrder, setSortOrder] = useState<0 | 1 | -1 | null>(-1);

  /* ---------------------------------------------------------------------- */
  /* Data loading                                                            */
  /* ---------------------------------------------------------------------- */

  const loadOrders = useCallback(async () => {
    setLoading(true);

    try {
      const [from, to] = dateRange ?? [];
      // Consulta días UTC para la API actual y después ajusta el resultado a los límites exactos.
      const rangeStart =
        from && to ? getUtcDayRange(from, selectedTimezone).start : undefined;
      const rangeEnd =
        from && to
          ? getUtcDayRange(to, selectedTimezone).endExclusive
          : undefined;

      const { data } =
        await apiServices.maintenanceOrderApi.maintenanceOrdersControllerGetAllMaintenanceOrders(
          statusFilter,
          rangeStart?.toISOString().slice(0, 10),
          rangeEnd
            ? new Date(rangeEnd.getTime() - 1).toISOString().slice(0, 10)
            : undefined
        );

      const ordersInRange =
        rangeStart && rangeEnd
          ? (data ?? []).filter((order) => {
              const timestamp = new Date(
                String(order.creation_date)
              ).getTime();
              return (
                !Number.isNaN(timestamp) &&
                timestamp >= rangeStart.getTime() &&
                timestamp < rangeEnd.getTime()
              );
            })
          : data ?? [];

      setOrders(ordersInRange);
    } catch (error) {
      console.error('Error loading maintenance orders:', error);
      setError(true);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedTimezone, statusFilter, setError]);

  // Loads on mount, whenever the toolbar changes the range or the scope, and
  // when the parent bumps refreshKey. A half-picked range is ignored.
  useEffect(() => {
    const [from, to] = dateRange ?? [];
    if (dateRange && (!from || !to)) return;

    setFirst(0);
    loadOrders();
  }, [dateRange, loadOrders, refreshKey]);

  /* ---------------------------------------------------------------------- */
  /* Derived data                                                            */
  /* ---------------------------------------------------------------------- */

  const rows: MaintenanceOrderRow[] = useMemo(
    () =>
      orders.map((order) => ({
        ...order,
        creationDate: parseDate(
          String(order.creation_date),
          selectedTimezone
        ),
      })),
    [orders, selectedTimezone]
  );


  const statusOptions = useMemo(
    () =>
      Array.from(
        new Set(
          orders
            .map((order) => (order.status ?? '').trim())
            .filter((value) => value.length > 0)
        )
      ).map((value) => ({ label: value, value })),
    [orders]
  );

  /* ---------------------------------------------------------------------- */
  /* Actions                                                                 */
  /* ---------------------------------------------------------------------- */

  // The range and the scope are owned by the page toolbar; the column filters
  // are not, so they reset on the toolbar's signal instead.
  useEffect(() => {
    if (!clearFiltersKey) return;

    setFilters(buildInitialFilters());
    setFirst(0);
  }, [clearFiltersKey]);

  const handlePageChange = (event: DataTablePageEvent) => {
    setFirst(event.first);
    setPageSize(event.rows);
  };

  const openOrderDetails = (row: MaintenanceOrderRow) => {
    navigate(`/authenticated/maintenance-order/${row.id}`, {
      state: { maintenance_order: row },
    });
  };

  /* ---------------------------------------------------------------------- */
  /* Templates                                                               */
  /* ---------------------------------------------------------------------- */

  const statusFilterTemplate = (options: {
    value: string | null;
    filterApplyCallback: (value: string | null) => void;
  }) => (
    <Dropdown
      value={options.value}
      options={statusOptions}
      onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder={t('pages.incidents.search_status')}
      className="p-column-filter"
      showClear
      style={{ minWidth: '12rem' }}
      itemTemplate={(option: { label: string; value: string }) => (
        <Tag
          style={{
            paddingLeft: '0.5rem',
            paddingRight: '0.5rem',
            backgroundColor: getIncidentStatusColor(
              normalizeStatus(option.value)
            ),
            color: 'white',
          }}
          value={option.label}
        />
      )}
    />
  );

  /* ---------------------------------------------------------------------- */
  /* Render                                                                 */
  /* ---------------------------------------------------------------------- */

  return (
    <div>
      <DataTable
        value={rows}
        loading={loading}
        dataKey="id"
        paginator
        selectionMode="single"
        onSelectionChange={(e) => e.value && openOrderDetails(e.value as MaintenanceOrderRow)}
        first={first}
        rows={pageSize}
        rowsPerPageOptions={[5, 10, 25, 50]}
        onPage={handlePageChange}
        removableSort
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={(e) => {
          setSortField(e.sortField);
          setSortOrder(e.sortOrder as 0 | 1 | -1 | null);
          setFirst(0);
        }}
        filters={filters}
        onFilter={(e) => {
          setFilters(e.filters);
          setFirst(0);
        }}
        header={header}
        emptyMessage={t('emptyValuesTable')}
      >
        <Column
          field="code"
          header={t('pages.maintenance_orders.code')}
          style={{ width: '14rem' }}
          className="col-12 sm:col-4 md:col-4 xl:col-3"
          filter
          filterPlaceholder={t('pages.maintenance_orders.code')}
          showFilterMatchModes={false}
          body={(row: MaintenanceOrderRow) => row.code ?? row.id}
          sortable
        />

        <Column
          field="description"
          header={t('pages.incidents.description')}
          style={{ width: '14rem', minWidth: '14rem', maxWidth: '14rem' }}
          className="col-12 sm:col-6 md:col-4 xl:col-3"
          filter
          filterPlaceholder={t('pages.incidents.description')}
          showFilterMatchModes={false}
          body={(row: MaintenanceOrderRow) => (
            <div
              className="block white-space-nowrap overflow-hidden text-overflow-ellipsis"
              title={row.description ?? ''}
            >
              {row.description}
            </div>
          )}
        />

        <Column
          field="device_serial_number"
          header={t('pages.incidents.sn_product')}
          style={{ width: '15rem', minWidth: '15rem' }}
          className="col-12 sm:col-6 md:col-4 xl:col-3"
          filter
          filterPlaceholder={t('pages.incidents.sn_product')}
          showFilterMatchModes={false}
          body={(row: MaintenanceOrderRow) =>
            getDeviceDisplayValue(
              { ...row, serial_number: row.device_serial_number },
              deviceNames
            )
          }
          sortable
        />

        <Column
          field="status"
          header={t('pages.incidents.status')}
          style={{ width: '16rem' }}
          className="col-12 sm:col-6 md:col-4 xl:col-3"
          body={(row: MaintenanceOrderRow) =>
            incidenceBodyTemplate(
              row,
              'status',
              getIncidentPriorityColor,
              getIncidentStatusColor
            )
          }
          filter
          filterElement={statusFilterTemplate}
          showFilterMatchModes={false}
          sortable
        />

        <Column
          field="responsible"
          header={t('pages.incidents.createMaintenanceOrder.responsible_label')}
          style={{ minWidth: 'max-content' }}
          headerStyle={{ whiteSpace: 'nowrap' }}
          bodyStyle={{ whiteSpace: 'nowrap' }}
          filter
          filterPlaceholder={t(
            'pages.incidents.createMaintenanceOrder.responsible_label'
          )}
          showFilterMatchModes={false}
          body={(row: MaintenanceOrderRow) =>
            row.responsible ?? t('pages.maintenance_orders.no_data')
          }
          sortable
        />

        <Column
          field="creationDate"
          dataType="date"
          header={t('pages.maintenance_orders.creation_date')}
          style={{ width: '18rem', minWidth: '15rem' }}
          className="col-12 sm:col-6 md:col-4 xl:col-3"
          filter
          filterElement={(options) => <DateFilterTemplate options={options} />}
          showFilterMatchModes={false}
          showFilterOperator={false}
          showAddButton={false}
          body={(row: MaintenanceOrderRow) => (
            <div className="pl-3">
              {formatDateTime(
                String(row.creation_date),
                selectedTimezone,
                i18n.language
              )}
            </div>
          )}
          sortable
        />
      </DataTable>
    </div>
  );
}

export default MaintenanceOrdersList;
